import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";
import {defaultSupplier} from "../generic/InitialStates";
import {AGCText} from "../../generic-components/AGCText";
import {AGCTextArea} from "../../generic-components/AGCTextArea";
import {apiGenericCreate, apiGenericUpdate, genericGetOne, getAllOrgSettings} from "../generic/crud";
import {ENTITY_ID_SUPPLIER} from "../generic/Constants";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {GstinSmartFill} from "../../generic-components/GstinSmartFill";

export function SupplierAddEditDialog(props) {
    const {show, handleClose, id} = props;
    const [objectForSubmit, setObjectForSubmit] = useState(defaultSupplier);
    const [isLoading, setIsLoading] = useState(false);
    const [forUpdate, setForUpdate] = useState(false);
    const [title, setTitle] = useState("Supplier");
    const [allowOpeningBalance, setAllowOpeningBalance] = useState(false);

    const entityName = "Supplier";

    // Define validation schema for the form
    const validationSchema = Yup.object({
        name: Yup.string().required("Supplier Name is required"),
        phone: Yup.string(),
        address: Yup.string(),
        pinCode: Yup.string(),
        gstNumber: Yup.string(),
        panNumber: Yup.string(),
        billingName: Yup.string(),
    });

    const formik = useFormik({
        initialValues: objectForSubmit,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit(supplier, {resetForm}) {
            setIsLoading(true);
            supplier.creditLimit = 0;

            if (!id) {
                apiGenericCreate(ENTITY_ID_SUPPLIER, supplier).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Created!", TOAST_SUCCESS);
                    resetForm();
                }).catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error creating " + entityName + ": " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
            } else {
                apiGenericUpdate(ENTITY_ID_SUPPLIER, supplier, id).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Updated!", TOAST_SUCCESS);
                    resetForm();
                }).catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error updating " + entityName + ": " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
            }
        }
    });

    useEffect(() => {
        getAllOrgSettings().then((res) => {
            const settings = res?.data || [];
            const s = settings.find(s => s.typeName === 'ALLOW_OPENING_BALANCE_ON_CLIENT');
            setAllowOpeningBalance(s ? (s.value === true || s.value === 'true') : false);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (id != null) {
            setForUpdate(true);
            setTitle("Edit " + entityName);
            setIsLoading(true);
            genericGetOne(ENTITY_ID_SUPPLIER, id).then(
                ({data}) => {
                    setObjectForSubmit(data);
                    setTitle("Update " + entityName);
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                    showToast(
                        "Error fetching " + entityName + " data: " + (error.message || "Unknown error"),
                        TOAST_FAILURE
                    );
                });
        } else {
            setForUpdate(false);
            setObjectForSubmit(defaultSupplier);
            setTitle("New " + entityName);
        }
    }, [show]);

    // Fill the form from a public GST record (blank fetched values keep what's typed).
    const applyGstDetails = (details) => {
        const v = formik.values;
        formik.setValues({
            ...v,
            gstNumber: details.gstin || v.gstNumber,
            name: details.tradeName || details.legalName || v.name,
            billingName: details.legalName || v.billingName,
            address: details.address || v.address,
            pinCode: details.pincode || v.pinCode,
            panNumber: details.pan || v.panNumber,
        });
        showToast("Business details filled from GST records — please review", TOAST_SUCCESS);
    };

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            aria-labelledby="example-modal-sizes-title-lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading ? (
                    <>
                        <Form className="form form-label-right" onSubmit={formik.handleSubmit}>
                            <GstinSmartFill
                                value={formik.values.gstNumber}
                                onChange={(v) => formik.setFieldValue("gstNumber", v)}
                                onApply={applyGstDetails}
                            />

                            <div className="form-group row">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Supplier Name"}
                                        placeHolder={"Enter Supplier Name"}
                                        id={"name"}
                                        toolTip={"Please enter the Supplier Name"}
                                        value={formik.values.name}
                                        required={true}
                                    />
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Billing Name"}
                                        placeHolder={"Enter Billing Name (Optional)"}
                                        id={"billingName"}
                                        toolTip={"Please enter the Billing Name (Optional)"}
                                        value={formik.values.billingName}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="form-group row">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Phone"}
                                        placeHolder={"Enter Phone Number"}
                                        id={"phone"}
                                        toolTip={"Please enter the Phone Number"}
                                        value={formik.values.phone}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="form-group row">
                                <Form.Group className="col-lg-12">
                                    <AGCTextArea
                                        formik={formik}
                                        name={"Address"}
                                        placeHolder={"Enter Full Address"}
                                        id={"address"}
                                        toolTip={"Please enter the Full Address"}
                                        value={formik.values.address}
                                        rows={3}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="form-group row">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Pin Code"}
                                        placeHolder={"Enter Pin Code"}
                                        id={"pinCode"}
                                        toolTip={"Please enter the Pin Code"}
                                        value={formik.values.pinCode}
                                        required={false}
                                    />
                                </Form.Group>

                                {allowOpeningBalance && (
                                    <Form.Group className="col-lg-6">
                                        <AGCText
                                            formik={formik}
                                            name={"Opening Balance"}
                                            placeHolder={"Enter Opening Balance (Optional)"}
                                            id={"openingBalance"}
                                            toolTip={"Enter the opening balance if this supplier has prior transactions"}
                                            value={formik.values.openingBalance}
                                            inputType={"number"}
                                            required={false}
                                        />
                                    </Form.Group>
                                )}
                            </div>

                            <div className="form-group row">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"GST Number"}
                                        placeHolder={"Enter GST Number (Optional)"}
                                        id={"gstNumber"}
                                        toolTip={"Please enter the GST Number (Optional)"}
                                        value={formik.values.gstNumber}
                                        required={false}
                                    />
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"PAN Number"}
                                        placeHolder={"Enter PAN Number (Optional)"}
                                        id={"panNumber"}
                                        toolTip={"Please enter the PAN Number (Optional)"}
                                        value={formik.values.panNumber}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            <Modal.Footer>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-elevate"
                                        onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <> </>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-elevate">
                                        Save
                                    </button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    </>
                ) : (
                    <div className="text-center p-4">
                        <span>Please Wait, Fetching Information from Server</span>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
